import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useSport } from '../context/SportContext';
import HeaderStats from '../components/HeaderStats';
import FilterSection from '../components/FilterSection';
import NHLConfidenceTiers from '../components/NHLConfidenceTiers';
import { enrichPredictionsWithResults } from '../utils/api';
const CBBDashboard = () => {
    console.log('🏀 CBBDashboard component is rendering');
    const { currentSport } = useSport();
    const [predictions, setPredictions] = useState(null);
    const [filteredGames, setFilteredGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        team: '',
        confidence: 'ALL',
        conference: 'ALL',
        week: 'ALL',
        date: 'ALL'
    });
    useEffect(() => {
        const loadPredictions = async () => {
            try {
                setLoading(true);
                const timestamp = Date.now();
                const response = await fetch(`/static/cbb/cbb_predictions.json?v=${timestamp}`, {
                    headers: {
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    }
                });
                console.log('🏀 Fetching CBB predictions with timestamp:', timestamp);
                if (!response.ok) {
                    throw new Error('Failed to fetch CBB predictions');
                }
                const data = await response.json();
                console.log('🏀 Loaded predictions data:', data);
                console.log('🏀 Data has predictions:', !!data?.predictions);
                console.log('🏀 Predictions count:', data?.predictions?.length);
                // Transform the data structure to match what the components expect
                const rawGames = data.predictions || [];
                console.log('🏀 First raw game:', rawGames[0]);
                const games = rawGames.map((game) => {
                    // Extract betting analysis
                    const bettingAnalysis = game.betting_analysis || {};
                    // Map confidence_category directly (VERY_LOW and missing both → LOW)
                    const rawCategory = bettingAnalysis.confidence_category || '';
                    let confidenceLevel;
                    if (rawCategory === 'HIGH')
                        confidenceLevel = 'HIGH';
                    else if (rawCategory === 'MEDIUM_PLUS')
                        confidenceLevel = 'MEDIUM_PLUS';
                    else if (rawCategory === 'MEDIUM')
                        confidenceLevel = 'MEDIUM';
                    else if (rawCategory === 'LOW')
                        confidenceLevel = 'LOW';
                    else
                        confidenceLevel = 'LOW'; // VERY_LOW, NO_LINE, or missing
                    const edge = bettingAnalysis.edge_percentage || 0;
                    // Determine team to cover based on recommendation
                    const recommendation = bettingAnalysis.recommendation;
                    const predictedSpread = game.predicted_spread || 0;
                    const bettingOddsRaw = game.betting_odds || {};
                    let teamToCover = game.home_team;
                    let predictedWinner = 'HOME';
                    let spreadLine = game.market_spread || 0;
                    // Prefer the explicit recommended_pick from the JSON, then
                    // spread_coverage_prediction, then betting recommendation fields.
                    const recommendedPick = game.recommended_pick;
                    const spreadCoverage = game.spread_coverage_prediction;
                    if (recommendedPick?.team && recommendedPick?.side) {
                        teamToCover = recommendedPick.team;
                        predictedWinner = recommendedPick.side; // 'HOME' or 'AWAY'
                        spreadLine = predictedWinner === 'HOME'
                            ? (bettingOddsRaw.home_spread ?? game.market_spread ?? 0)
                            : (bettingOddsRaw.away_spread ?? -(game.market_spread ?? 0));
                    }
                    else if (recommendation === 'BET_AWAY') {
                        teamToCover = game.away_team;
                        predictedWinner = 'AWAY';
                        spreadLine = bettingOddsRaw.away_spread ?? -(game.market_spread || 0);
                    }
                    else if (recommendation === 'BET_HOME') {
                        teamToCover = game.home_team;
                        predictedWinner = 'HOME';
                        spreadLine = bettingOddsRaw.home_spread ?? (game.market_spread || 0);
                    }
                    else if (spreadCoverage?.predicted_winner) {
                        // Use spread coverage prediction — this correctly reflects who covers,
                        // not just who wins outright.
                        predictedWinner = spreadCoverage.predicted_winner;
                        teamToCover = predictedWinner === 'HOME' ? game.home_team : game.away_team;
                        spreadLine = predictedWinner === 'HOME'
                            ? (bettingOddsRaw.home_spread ?? game.market_spread ?? 0)
                            : (bettingOddsRaw.away_spread ?? -(game.market_spread ?? 0));
                    }
                    else {
                        // Last-resort fallback (should rarely be reached)
                        predictedWinner = predictedSpread < 0 ? 'HOME' : 'AWAY';
                        teamToCover = predictedSpread < 0 ? game.home_team : game.away_team;
                        spreadLine = predictedSpread < 0
                            ? (bettingOddsRaw.home_spread ?? game.market_spread ?? 0)
                            : (bettingOddsRaw.away_spread ?? -(game.market_spread ?? 0));
                    }
                    // Calculate display confidence (0-100 scale)
                    const displayConfidence = (game.confidence || 0.5) * 100;
                    return {
                        id: game.game_id || `game_${Math.random()}`,
                        game_id: game.game_id || `game_${Math.random()}`,
                        home_team: game.home_team,
                        away_team: game.away_team,
                        game_date: game.date,
                        game_time: '7:00 PM',
                        venue: game.venue || `${game.home_team} Arena`,
                        week: 1, // CBB doesn't use weeks
                        year: 2025,
                        conference: game.home_conference,
                        // Prediction fields
                        confidence_level: confidenceLevel,
                        confidence: game.confidence || 0.5,
                        team_to_cover: teamToCover,
                        predicted_winner: predictedWinner,
                        predicted_spread: predictedSpread,
                        display_confidence: displayConfidence,
                        ensemble_probability: game.confidence || 0.5,
                        // Betting fields
                        spread: game.market_spread,
                        spread_line: spreadLine, // The actual spread for the picked team
                        betting_edge: edge / 100, // Convert percentage to decimal
                        recommendation: bettingAnalysis.recommendation,
                        bet_grade: bettingAnalysis.bet_grade,
                        confidence_category: bettingAnalysis.confidence_category,
                        model_agreement: game.model_agreement,
                        // Betting odds (moneyline, spread, totals)
                        home_ml: bettingOddsRaw.home_moneyline,
                        away_ml: bettingOddsRaw.away_moneyline,
                        home_spread: bettingOddsRaw.home_spread,
                        away_spread: bettingOddsRaw.away_spread,
                        home_spread_odds: bettingOddsRaw.home_spread_odds,
                        away_spread_odds: bettingOddsRaw.away_spread_odds,
                        total: bettingOddsRaw.total_points,
                        over_odds: bettingOddsRaw.over_odds,
                        under_odds: bettingOddsRaw.under_odds,
                        bookmaker: bettingOddsRaw.bookmaker,
                        // Individual model predictions (card checks individual_predictions)
                        individual_models: game.individual_models,
                        individual_predictions: game.individual_models,
                        // Feature analysis (key factors driving the prediction)
                        feature_analysis: game.feature_analysis,
                        // Store original data for reference
                        raw_data: game,
                        original_prediction_data: game
                    };
                });
                const transformedData = {
                    metadata: data.summary || data.metadata || data,
                    games: games
                };
                console.log('🏀 Transformed games sample:', games[0]);
                console.log('🏀 Games with IDs:', games.map((g) => ({ id: g.id, teams: `${g.away_team} @ ${g.home_team}` })));
                // Enrich with game results
                console.log('🏀 Enriching with game results...');
                const enrichedData = await enrichPredictionsWithResults(transformedData, 'cbb');
                console.log('🏀 Enriched data:', enrichedData);
                setPredictions(enrichedData);
                setFilteredGames(enrichedData.games);
            }
            catch (err) {
                setError('Failed to load CBB predictions. Please try again later.');
                console.error('Error loading CBB predictions:', err);
            }
            finally {
                setLoading(false);
            }
        };
        loadPredictions();
    }, [currentSport]);
    useEffect(() => {
        if (!predictions)
            return;
        let filtered = predictions.games;
        // Apply team filter
        if (filters.team) {
            const teamLower = filters.team.toLowerCase();
            filtered = filtered.filter((game) => game.home_team.toLowerCase().includes(teamLower) ||
                game.away_team.toLowerCase().includes(teamLower));
        }
        // Apply confidence filter
        if (filters.confidence !== 'ALL') {
            filtered = filtered.filter((game) => game.confidence_level === filters.confidence);
        }
        // Apply conference filter
        if (filters.conference !== 'ALL') {
            filtered = filtered.filter((game) => {
                return game.conference === filters.conference ||
                    game.raw_data?.home_conference === filters.conference ||
                    game.raw_data?.away_conference === filters.conference;
            });
        }
        console.log('🏀 CBB filtered games:', filtered.length);
        if (filtered.length > 0) {
            console.log('🏀 First game structure:', filtered[0]);
            console.log('🏀 Confidence levels in data:', [...new Set(filtered.map((g) => g.confidence_level))]);
        }
        setFilteredGames(filtered);
    }, [predictions, filters]);
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    };
    if (loading) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsx("div", { className: "d-flex justify-content-center", children: _jsx("div", { className: "spinner-border", role: "status", children: _jsx("span", { className: "visually-hidden", children: "Loading..." }) }) }) }));
    }
    if (error) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsxs("div", { className: "alert alert-danger", role: "alert", children: [_jsx("i", { className: "fas fa-exclamation-triangle me-2" }), error] }) }));
    }
    if (!predictions) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsx("div", { className: "alert alert-info", role: "alert", children: "No CBB predictions available at this time." }) }));
    }
    return (_jsxs("div", { className: "container-mobile safe-area-padding", children: [_jsx(HeaderStats, { metadata: predictions?.metadata, games: predictions?.games || [], sport: currentSport }), _jsx(FilterSection, { filters: filters, onFilterChange: handleFilterChange, availableWeeks: [], availableConferences: predictions?.games ?
                    [...new Set(predictions.games
                            .flatMap((g) => [
                            g.conference,
                            g.raw_data?.home_conference,
                            g.raw_data?.away_conference
                        ].filter(conf => conf && conf !== null)))].sort() : [] }), _jsx(NHLConfidenceTiers, { games: filteredGames })] }));
};
export default CBBDashboard;
